import { useState } from "react";
import { Clock4, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const toHm = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}`;
};
const fromHm = (v) => {
  const [h, m] = String(v || "0:0").split(":");
  return (parseInt(h, 10) || 0) * 3600 + (parseInt(m, 10) || 0) * 60;
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{label}</p>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder="tt:mm" className="mt-1 h-10" />
  </div>
);

export const MyTimePanel = ({ job, me, onSaved, readOnly }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  const start = () => {
    setForm({
      work: toHm(me?.work_seconds),
      travel_out: toHm(me?.travel_out_seconds),
      travel_home: toHm(me?.travel_home_seconds),
      reason: "",
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { reason: form.reason || null };
      const fields = [
        ["work_seconds", form.work, me?.work_seconds],
        ["travel_out_seconds", form.travel_out, me?.travel_out_seconds],
        ["travel_home_seconds", form.travel_home, me?.travel_home_seconds],
      ];
      fields.forEach(([key, next, current]) => {
        const secs = fromHm(next);
        if (secs !== Math.floor(current || 0)) body[key] = secs;
      });
      if (Object.keys(body).length === 1) {
        toast.info("Ingen ändring gjord");
        setOpen(false);
        return;
      }
      await api.put(`/jobs/${job.id}/my-time`, body);
      setOpen(false);
      await onSaved?.();
      toast.success("Din tid är uppdaterad");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="my-time-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex flex-1 items-center gap-2 text-xl font-semibold text-slate-800">
          <Clock4 className="h-4 w-4 text-slate-400" /> Min tid
        </h2>
        {!open && (
          <Button
            data-testid="my-time-edit-button"
            variant="outline"
            disabled={readOnly}
            onClick={start}
            className="h-10 bg-white transition-colors duration-200 hover:bg-slate-100"
          >
            Ändra min tid
          </Button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        Glömde du starta ordern? Fyll i din arbetstid och restid i efterhand. Ändringen sparas i
        orderns ändringshistorik.
      </p>

      {!open ? (
        <div className="mt-3 grid grid-cols-3 gap-2 text-sm" data-testid="my-time-current">
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <p className="text-xs uppercase tracking-[0.05em] text-slate-500">Arbetstid</p>
            <p className="font-heading font-bold tabular-nums text-slate-900" data-testid="my-time-work">{toHm(me?.work_seconds)}</p>
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <p className="text-xs uppercase tracking-[0.05em] text-slate-500">Restid dit</p>
            <p className="font-heading font-bold tabular-nums text-slate-900" data-testid="my-time-travel-out">{toHm(me?.travel_out_seconds)}</p>
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <p className="text-xs uppercase tracking-[0.05em] text-slate-500">Restid hem</p>
            <p className="font-heading font-bold tabular-nums text-slate-900" data-testid="my-time-travel-home">{toHm(me?.travel_home_seconds)}</p>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3" data-testid="my-time-form">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Field label="Arbetstid" value={form.work} onChange={(v) => setForm({ ...form, work: v })} testid="my-time-work-input" />
            <Field label="Restid dit" value={form.travel_out} onChange={(v) => setForm({ ...form, travel_out: v })} testid="my-time-travel-out-input" />
            <Field label="Restid hem" value={form.travel_home} onChange={(v) => setForm({ ...form, travel_home: v })} testid="my-time-travel-home-input" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Orsak (frivilligt)</p>
            <Input
              data-testid="my-time-reason-input"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="t.ex. glömde starta ordern"
              className="mt-1 h-10"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="my-time-save-button"
              disabled={busy}
              onClick={save}
              className="h-11 flex-1 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Spara min tid</>}
            </Button>
            <Button
              data-testid="my-time-cancel-button"
              variant="outline"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="h-11 bg-white transition-colors duration-200 hover:bg-slate-100"
            >
              Avbryt
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
